import { Component, ChangeDetectionStrategy } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, UntypedFormArray } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Subscription, map } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';
import { Features, ModuleNames } from '../../../common/model/account-definition';
import { UserAccountService } from '../../../common/service/user-account.service';
import { MeterMediaType } from '../../../consumption/model/const/enums';
import { Meter } from '../../../consumption/model/meter';
import { RentalObjectConsumptionMeter } from '../../../consumption/model/rentalobject-consumption-meter';
import { MeterService } from '../../../consumption/service/meter.service';
import { ColumnDefinition, ColumnVisibility } from '../../../core/model/column-definition';
import { standardCommand, Command } from '../../../core/model/command';
import { DataService } from '../../../core/service/base/data.service';
import { CommandDispatcher } from '../../../core/service/command/command-dispatcher.service';
import { DeleteFormCommandHandler } from '../../../core/service/command/delete-form-command-handler.service';
import { ColumnDefinitionFactory } from '../../../core/service/factory/column-definition-factory.service';
import { FormControlExFactory } from '../../../core/service/factory/form-control-ex-factory.service';
import { MessageService } from '../../../core/service/message.service';
import { NavigationService } from '../../../core/service/navigation.service';
import { FaultNotificationService } from '../../../fault-notification/service/data/fault-notification.service';
import { LettingAdvertisment } from '../../../letting/model/letting-advertisment';
import { LettingAdvertismentPremisesService } from '../../../letting/service/letting-advertisment-premises.service';
import { rentalObjectCommand } from '../../command/rental-object.command';
import { Building } from '../../model/building';
import { rentalObjectFormCommand } from '../../model/const/command';
import {
  CustomPropertyDataType,
  FormOfLease,
  FormOfLeaseToString,
  LeaseAgreementCategory,
  PropertyTaxCalculationBasis,
  PropertyTaxCalculationBasisToString,
  PropertyTaxCalculationMethod,
  PropertyTaxCalculationMethodToString,
  PropertyTaxPaidBy,
  PropertyTaxPaidByToString,
  RentalObjectCategory,
} from '../../model/const/enums';
import { Contract } from '../../model/contract';
import { Entrance } from '../../model/entrance';
import { LinkedCustomProperty } from '../../model/linked-custom-property';
import { Premises } from '../../model/premises';
import { RentApplication } from '../../model/rent-application';
import { CreateContractRentalObjectCommandHandler } from '../../service/command/create-contract-rental-object-command-handler.service';
import { RentalObjectFormCommandHandlerResolver } from '../../service/command/rental-object-form-command-handler-resolver.service';
import { BuildingService } from '../../service/data/building.service';
import { ColumnDefinitionService } from '../../service/data/column-definition.service';
import { CompanyService } from '../../service/data/company.service';
import { EntranceService } from '../../service/data/entrance.service';
import { PremisesService } from '../../service/data/premises.service';
import { PropertyService } from '../../service/data/property.service';
import { RentalObjectLogService } from '../../service/data/rental-object-log.service';
import { RentalObjectTypeService } from '../../service/data/rental-object-type.service';
import { PremisesDraftPropertyTaxCalculationRequest } from '../../service/data/premises-contract.service';
import { IndexValueService } from '../../service/data/index-value.service';
import { PropertyTaxCalculationService, PropertyTaxDraftCoordinator } from '../../service/data/property-tax-calculation.service';
import { RentalObjectFormComponent } from '../base/rental-object.base';
import { SelectionItem } from '../../../core/component/select/select.component';
import { Money } from '../../../core/model/money';
import { LicenseService } from '../../../common/service/data/license.service';
import { Responsibility } from '../../model/premises-contract';
import { DimensionLabelsService } from '../../../accounting/service/data/dimension-labels.service';
import { LeaseRow } from '../../model/lease-row';
import { Article } from '../../model/article';
import { LocalDate } from '../../../core/model/local-date';
import { LanguageService } from '../../../core/service/language.service';
import { LeaseAgreementTemplate } from '../../model/lease-agreement-template';
import { PropertyTaxRentalChangeModel } from '../../model/property-tax-rental-change';
import {
  applyResolvedPropertyTaxMutationResult,
  buildPropertyTaxRowSignature,
  getCurrentUiPropertyTaxReplacementRow,
  getLatestActivePropertyTaxRow,
  getPropertyTaxAssessmentUnitTypeString,
  isPersistedLeaseRow,
  PropertyTaxRowSignature,
  resolvePropertyTaxRowMutation,
  runWithPropertyTaxUserTriggersSuspended,
  shouldHandlePropertyTaxUserChange,
  updatePropertyTaxRowInline,
} from '../../helper/property-tax.helpers';
import {
  getPersistedPropertyTaxRowsSnapshot,
  havePersistedPropertyTaxRowsChanged,
  PropertyTaxChangeOrigin,
  PropertyTaxDraftResult,
  PropertyTaxFormState,
} from '../../helper/property-tax-form-state';

const formControlName = {
  building: 'building',
  buildingFloors: 'buildingFloors',
  elevator: 'elevator',
  floor: 'floor',
  indvidualmeterelectricity: 'indvidualmeterelectricity',
  indvidualmeterwater: 'indvidualmeterwater',
  indvidualmetercoldwater: 'indvidualmetercoldwater',
  indvidualmeterheating: 'indvidualmeterheating',
  numberOfRooms: 'numberOfRooms',
  rentalObjectType: 'rentalObjectType',
  entrance: 'entrance',
};

type PropertyTaxDirtyIntent = {
  rentalObjectId: string | null;
  propertyTaxPaidBy: number | null;
  propertyTaxCalculationBasis: number | null;
  propertyTaxPercentage: number | null;
  propertyTaxCalculationMethod: number | null;
  propertyTaxArticleId: string | null;
  propertyTaxRetroactiveArticleId: string | null;
};

@Component({
    selector: 'app-premises-form',
    templateUrl: './premises-form.component.html',
    styleUrls: ['./premises-form.component.scss'],
    providers: [
        DeleteFormCommandHandler,
        CreateContractRentalObjectCommandHandler,
        RentalObjectFormCommandHandlerResolver,
        IndexValueService,
        PropertyTaxCalculationService,
        { provide: 'category', useValue: RentalObjectCategory.Premises },
        { provide: DataService, useExisting: PremisesService },
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class PremisesFormComponent extends RentalObjectFormComponent<Premises> {
  private readonly _propertyTaxFormState = new PropertyTaxFormState();

  protected override get category(): RentalObjectCategory {
    return RentalObjectCategory.Premises;
  }

  constructor(
    private _buildingService: BuildingService,
    private _entranceService: EntranceService,
    private _cdf: ColumnDefinitionFactory,
    private _meterService: MeterService,
    activatedRoute: ActivatedRoute,
    companyService: CompanyService,
    private _dataService: PremisesService,
    dispatcher: CommandDispatcher,
    cds: ColumnDefinitionService,
    fb: FormBuilder,
    private cf: FormControlExFactory,
    handlerResolver: RentalObjectFormCommandHandlerResolver,
    messageService: MessageService,
    navigation: NavigationService,
    propertyService: PropertyService,
    rentalObjectTypeService: RentalObjectTypeService,
    faultNotificationService: FaultNotificationService,
    columnDefinitionService: ColumnDefinitionService,
    logService: RentalObjectLogService,
    private readonly _accountService: UserAccountService,
    private readonly advertismentService: LettingAdvertismentPremisesService,
    licenseService: LicenseService,
    dimensionLabelsService: DimensionLabelsService,
    private _languageService: LanguageService,
    private readonly _propertyTaxCalculationService: PropertyTaxCalculationService
  ) {
    super(
      fb,
      cf,
      companyService,
      propertyService,
      rentalObjectTypeService,
      faultNotificationService,
      cds,
      activatedRoute,
      dispatcher,
      handlerResolver,
      _dataService,
      messageService,
      navigation,
      columnDefinitionService,
      logService,
      licenseService,
      dimensionLabelsService
    );

    this.AddSubscription(
      this._accountService.current.subscribe({
        next: account => {
          if (account.hasLeaseAgreementWriteAccess()) {
            this.addMenuItemDefinition('VACANT_RENTAL_OBJECT_LIST.CREATE_LEASE_AGREEMENT_BTN', rentalObjectFormCommand.createContract);
          }
          this.addMenuItemDefinition('LEASE_AGREEMENT.COMMAND.SHOW_INVOICES', rentalObjectCommand.showInvoices);
          if (account.hasAccess(ModuleNames.Letting, Features.Letting_DhMarket)) {
            this.addMenuItemDefinition('RENTAL_OBJECT.COMMAND.ADVERTISE', rentalObjectCommand.advertise);
            this.showAdverts = true;
          }
          if (
            account.hasAccess(ModuleNames.Properties, Features.LinkPropertyBaseDocuments) ||
            account.hasAccess(ModuleNames.Properties, Features.EditPremises)
          ) {
            this.addMenuItemDefinition('LEASE_AGREEMENT.COMMAND.LINK_DOCUMENT', rentalObjectCommand.linkDocument);
          }
          if (account.hasAccess(ModuleNames.Properties, Features.EditPremises)) {
            this.addMenuItemDefinition('CREATE_BTN', standardCommand.create);
            this.addMenuItemDefinition('DELETE_BTN', standardCommand.delete);
          }
          if (account.hasAccess(ModuleNames.Properties, Features.ChangeRentalObjectCategory)) {
            const addChangeCategory = () => {
              if (this.dataContext?.id) this.addMenuItemDefinition('RENTAL_OBJECT_CATEGORY.COMMAND', rentalObjectCommand.changeCategory);
            };
            addChangeCategory();
            this.AddSubscription(this.dataContextInitialized$.subscribe(addChangeCategory));
          }
        },
      })
    );

    this.leaseAgreementColumnDefinition = [
      this._cdf.createTranslation('LEASE_AGREEMENT.STATUS', 'statusCaption'),
      this._cdf.create('LEASE_AGREEMENT.LEASE_AGREEMENT_NUMBER', 'leaseAgreementNumber'),
      this._cdf.createDate('LEASE_AGREEMENT.FROM_DATE', 'fromDate'),
      this._cdf.createDate('LEASE_AGREEMENT.TO_DATE', 'lastBillingDate'),
      this._cdf.create('LEASE_AGREEMENT.TENANT_ONE', 'tenantOne'),
      this._cdf.createMoney('LEASE_AGREEMENT.RENT_PERIOD_AMOUNT_CURRENT', 'currentPeriodRentAmount', ColumnVisibility.optional),
      this._cdf.createMoney('LEASE_AGREEMENT.RENT_PERIOD_AMOUNT_NEXT', 'nextPeriodRentAmount'),
      this._cdf.createMoney('COMMON.YEAR_RENT', 'totalYearRent'),
      this._cdf.createMoney('COMMON.YEAR_RENT_INCL_VAT', 'totalYearRentInclVat', ColumnVisibility.optional),
      this._cdf.createMoney('COMMON.YEAR_RENT_PER_SQM', 'totalYearRentPerSqm', ColumnVisibility.optional),
      this._cdf.createMoney('LEASE_AGREEMENT_ROW.YEAR_AMOUNT_IN_CURRENT_YEAR', 'chargedYearRentThisYear', ColumnVisibility.optional),
      this._cdf.createMoney(
        'LEASE_AGREEMENT_ROW.YEAR_AMOUNT_IN_CURRENT_YEAR_INCL_VAT',
        'chargedYearRentThisYearInclVat',
        ColumnVisibility.optional
      ),
      this._cdf.create('LEASE_AGREEMENT.INVOICE_PERIOD', 'invoicePeriodText', ColumnVisibility.optional),
    ];

    this.vacantPeriodsColumnDefinition = [
      this._cdf.createDate('VACANT_RENTAL_OBJECT_LIST.VACANT_PERIOD_FROM', 'fromDate'),
      this._cdf.createDate('VACANT_RENTAL_OBJECT_LIST.VACANT_PERIOD_TO', 'toDate'),
      this._cdf.createMoney('COMMON.RENT_LOSS_THIS_YEAR', 'rentLossCurrentYear'),
    ];

    this.rentApplicationsColumnDefinition = [
      this._cdf.createDate('RENT_APPLICATION.REGISTRATION_DATE', 'applicationDate', ColumnVisibility.default),
      this._cdf.create('TENANT.CONTACT_NUMBER', 'contactCustomerNumber', ColumnVisibility.optional),
      this._cdf.create('TENANT.NAME', 'contactName', ColumnVisibility.mandatory),
      this._cdf.create('TENANT.CIVIC_ORG_NUMBER', 'contactIdentification', ColumnVisibility.optional),
      this._cdf.create('TENANT.EMAIL', 'contactEmail', ColumnVisibility.default),
      this._cdf.create('TENANT.PHONE', 'contactPhone', ColumnVisibility.default),
      this._cdf.create('COMMON.NOTE', 'firstPartOfNote', ColumnVisibility.default),
    ];

    this.advertismentsColumnDefinition = [
      this._cdf.createDate('LETTING.ADVERTISMENT.CREATED_AT', 'createdAt', ColumnVisibility.mandatory),
      this._cdf.createTranslation('LETTING.ADVERTISMENT.PLATFORM', 'platformName', ColumnVisibility.mandatory),
      this._cdf.createDate('LETTING.ADVERTISMENT.PUBLISH_FROM', 'publishFrom', ColumnVisibility.default),
      this._cdf.createDate('LETTING.ADVERTISMENT.PUBLISH_TO', 'publishTo', ColumnVisibility.default),
      this._cdf.createDate('LETTING.ADVERTISMENT.VACANT_FROM', 'vacancyFrom', ColumnVisibility.default),
      this._cdf.createDate('LETTING.ADVERTISMENT.VACANT_TO', 'vacancyTo', ColumnVisibility.optional),
    ];
  }

  leaseAgreementColumnDefinition: ColumnDefinition[];
  vacantPeriodsColumnDefinition: ColumnDefinition[];
  rentApplicationsColumnDefinition: ColumnDefinition[];
  advertismentsColumnDefinition: ColumnDefinition[];
  showAdverts: boolean = false;
  advertisments: LettingAdvertisment[];
  rentalObjectDocumentCount = 0;

  consumptionIsExpanded = false;
  consumptionExpanded(expanded: boolean): void {
    this.consumptionIsExpanded = true;
  }

  mediaTypeColdWater = MeterMediaType.ColdWater;
  mediaTypeElectricity = MeterMediaType.Electricity;
  mediaTypeHotWater = MeterMediaType.WarmWater;
  mediaTypeHeating = MeterMediaType.Heating;
  changedAt: Date;

  linkDocument() {
    const cmd = new Command<Premises>(rentalObjectCommand.linkDocument);
    cmd.context = this.dataContext;
    this.commandDispatcher.sendCommand(cmd);
  }

  get propertyId(): string {
    let control = this.form.controls['property'];
    return control ? control.value : null;
  }

  private getActiveNonRetroPropertyTaxRow(): LeaseRow | undefined {
    const rows = this.dataContext?.leaseAgreementRowTemplates ?? [];

    if (this._nonRetroPropertyTaxRowRef && rows.includes(this._nonRetroPropertyTaxRowRef)) {
      return this._nonRetroPropertyTaxRowRef;
    }

    const active = getLatestActivePropertyTaxRow(rows);
    this._nonRetroPropertyTaxRowRef = active;
    return active;
  }

  private updatePropertyTaxRowInline(
    row: LeaseRow,
    selectedArticle: Article,
    propertyTaxChange: PropertyTaxRentalChangeModel | undefined,
    startDate?: LocalDate,
    options?: { forceHydrateCalculatedAmounts?: boolean; overwritePropertyTaxAmounts?: boolean }
  ): void {
    const context = this.getPropertyTaxContext();
    updatePropertyTaxRowInline(row, selectedArticle, propertyTaxChange, this._languageService, {
      ...context,
      startDate,
      forceHydrateCalculatedAmounts: options?.forceHydrateCalculatedAmounts,
      overwritePropertyTaxAmounts: options?.overwritePropertyTaxAmounts,
    });
  }

  private resetPropertyTaxUiState(): void {
    this._uiPropertyTaxRowRef = undefined;
    this._uiPropertyTaxRowCreatedByUi = false;
    this._nonRetroPropertyTaxRowRef = undefined;
    this._nonRetroPropertyTaxRowCreatedByUi = false;
  }

  private setNonRetroRow(row: LeaseRow, createdByUi: boolean): void {
    this._nonRetroPropertyTaxRowRef = row;
    this._nonRetroPropertyTaxRowCreatedByUi = createdByUi;
  }

  private getPropertyTaxContext(): { currencyCode?: string; rentalObjectSize?: number; invoicePeriod?: number } {
    return {
      currencyCode: this.dataContext?.currencyCode,
      rentalObjectSize: this.dataContext?.surface,
      invoicePeriod: this.templateForm?.get('invoicePeriodMonths')?.value ?? this.dataContext?.leaseAgreementTemplate?.invoicePeriodMonths,
    };
  }

  private getBaselinePropertyTaxSignature(): PropertyTaxRowSignature | null {
    if (!this._baselinePropertyTaxRowRef) return null;

    return buildPropertyTaxRowSignature(
      this._baselinePropertyTaxArticleId ?? null,
      this._baselinePropertyTaxCalculationBasis ?? null,
      this._baselinePropertyTaxCalculationMethod ?? null
    );
  }

  private bindPropertyTaxArticleChange(): void {
    this._propertyTaxArticleChangeSub?.unsubscribe();

    const control = this.templateForm?.get('propertyTaxArticlesNonRetroactive');
    if (!control) return;

    this._propertyTaxArticleChangeSub = control.valueChanges.subscribe(articleId => {
      this.handleCurrentPropertyTaxSelectionChange('user');
      this.scheduleDraftPropertyTaxRecalculation('user');
    });

    this.AddSubscription(this._propertyTaxArticleChangeSub);
  }

  private bindPropertyTaxDraftRecalculation(): void {
    const percentageControl = this.templateForm?.get('propertyTaxPercentage');
    const calculationMethodControl = this.templateForm?.get('propertyTaxCalculationMethod');
    const retroactiveArticleControl = this.templateForm?.get('propertyTaxArticlesRetroactive');

    if (percentageControl) {
      this.AddSubscription(
        percentageControl.valueChanges
          .pipe(
            tap(() => {
              this.scheduleDraftPropertyTaxRecalculation('user');
            })
          )
          .subscribe()
      );
    }

    if (calculationMethodControl) {
      this.AddSubscription(
        calculationMethodControl.valueChanges
          .pipe(
            tap(() => {
              this.scheduleDraftPropertyTaxRecalculation('user');
            })
          )
          .subscribe()
      );
    }

    if (retroactiveArticleControl) {
      this.AddSubscription(
        retroactiveArticleControl.valueChanges
          .pipe(
            tap(() => {
              this.scheduleDraftPropertyTaxRecalculation('user');
            })
          )
          .subscribe()
      );
    }
  }

  private scheduleDraftPropertyTaxRecalculation(changeOrigin: PropertyTaxChangeOrigin = 'user'): void {
    this._propertyTaxFormState.setPendingDraftChangeOrigin(changeOrigin);
    this._propertyTaxDraftCoordinator?.schedule();
  }

  private createDraftPropertyTaxRequest(): PremisesDraftPropertyTaxCalculationRequest | null {
    return this._propertyTaxCalculationService.buildDraftPropertyTaxRequest(
      {
        propertyId: this._propertyTaxCalculationService.normalizeId(this.dataContext?.propertyId),
        rentalObjectId: this._propertyTaxCalculationService.normalizeId(this.dataContext?.id),
        leaseAgreementId: null,
        propertyTaxPayer: this.templateForm?.get('propertyTaxPaidBy')?.value ?? null,
        propertyTaxCalculationBasis: this.templateForm?.get('propertyTaxCalculationBasis')?.value ?? null,
        propertyTaxPercentageValue: this.templateForm?.get('propertyTaxPercentage')?.value ?? null,
        propertyTaxCalculationMethod: this.templateForm?.get('propertyTaxCalculationMethod')?.value ?? null,
        propertyTaxArticleId: this.templateForm?.get('propertyTaxArticlesNonRetroactive')?.value ?? null,
        propertyTaxRetroactiveArticleId: this.templateForm?.get('propertyTaxArticlesRetroactive')?.value ?? null,
      },
      PropertyTaxPaidBy.Tenant
    );
  }

  private handlePropertyTaxNonRetroArticleChange(
    articleId: string | null,
    currentSignature: PropertyTaxRowSignature,
    changeOrigin: PropertyTaxChangeOrigin
  ): void {
    this.dataContext.leaseAgreementRowTemplates = this.dataContext.leaseAgreementRowTemplates ?? [];
    const rowsBeforeMutation = getPersistedPropertyTaxRowsSnapshot(this.dataContext.leaseAgreementRowTemplates);
    this.initBaselinePropertyTaxRow();
    const selectedArticle = articleId
      ? (this.dataContext?.leaseAgreementTemplate?.propertyTaxArticles ?? []).find(a => (a as any)?.id === articleId)
      : undefined;
    const propertyTaxChange = this.propertyTaxRentalChangeForLeaseRows;
    const today = LocalDate.today();
    const result = resolvePropertyTaxRowMutation(this.dataContext.leaseAgreementRowTemplates, {
      articleId,
      selectedArticle,
      propertyTaxRentalChange: propertyTaxChange,
      startDate: today,
      today,
      context: this.getPropertyTaxContext(),
      languageService: this._languageService,
      baselineRow: this._baselinePropertyTaxRowRef,
      baselineOriginalToDate: this._baselinePropertyTaxRowOriginalToDate,
      baselineRowClosedByUi: this._baselinePropertyTaxRowClosedByUi,
      baselineSignature: this.getBaselinePropertyTaxSignature(),
      currentSignature,
      activeRow: this.getActiveNonRetroPropertyTaxRow(),
      uiReplacementRow: getCurrentUiPropertyTaxReplacementRow(this.dataContext.leaseAgreementRowTemplates, {
        uiReplacementRowRef: this._uiPropertyTaxRowRef,
        nonRetroRowRef: this._nonRetroPropertyTaxRowRef,
      }),
    });
    applyResolvedPropertyTaxMutationResult(result, {
      setBaselineRowClosedByUi: value => (this._baselinePropertyTaxRowClosedByUi = value),
      setUiReplacementRow: row => (this._uiPropertyTaxRowRef = row),
      setUiReplacementRowCreatedByUi: value => (this._uiPropertyTaxRowCreatedByUi = value),
      setNonRetroRow: (row, createdByUi) => this.setNonRetroRow(row, createdByUi),
      resetPropertyTaxUiState: () => this.resetPropertyTaxUiState(),
    });
    this.ensureMarkDirtyAndRefreshRows(
      changeOrigin,
      havePersistedPropertyTaxRowsChanged(rowsBeforeMutation, this.dataContext.leaseAgreementRowTemplates)
    );
  }

  private handleCurrentPropertyTaxSelectionChange(changeOrigin: PropertyTaxChangeOrigin = 'user'): void {
    if (!shouldHandlePropertyTaxUserChange(this._suspendPropertyTaxUserTriggers)) return;

    const articleId = this.templateForm?.get('propertyTaxArticlesNonRetroactive')?.value ?? null;
    this.handlePropertyTaxNonRetroArticleChange(articleId, this.getCurrentPropertyTaxBaselineSignature(articleId), changeOrigin);
  }

  private applyDraftPropertyTaxToCurrentRow(
    articleId: string,
    propertyTaxChange: PropertyTaxRentalChangeModel,
    changeOrigin: PropertyTaxChangeOrigin
  ): void {
    const selectedArticle = (this.dataContext?.leaseAgreementTemplate?.propertyTaxArticles ?? []).find(a => (a as any)?.id === articleId);
    if (!selectedArticle) return;

    const today = LocalDate.today();
    const existingRow = this.getActiveNonRetroPropertyTaxRow();
    const targetRow = existingRow ?? this._baselinePropertyTaxRowRef;
    if (!targetRow) return;
    const rowsBeforeMutation = getPersistedPropertyTaxRowsSnapshot(this.dataContext.leaseAgreementRowTemplates);

    this.updatePropertyTaxRowInline(targetRow, selectedArticle, propertyTaxChange, today, {
      forceHydrateCalculatedAmounts: isPersistedLeaseRow(targetRow),
      overwritePropertyTaxAmounts: true,
    });

    this.setNonRetroRow(targetRow, !isPersistedLeaseRow(targetRow));
    this.ensureMarkDirtyAndRefreshRows(
      changeOrigin,
      havePersistedPropertyTaxRowsChanged(rowsBeforeMutation, this.dataContext.leaseAgreementRowTemplates)
    );
    this.refreshPropertyTaxRowCurrentYearAmounts(targetRow, changeOrigin);
  }

  private refreshPropertyTaxRowCurrentYearAmounts(row: LeaseRow, changeOrigin: PropertyTaxChangeOrigin): void {
    this._propertyTaxCurrentYearRentSub?.unsubscribe();
    this._propertyTaxCurrentYearRentSub = this._propertyTaxCalculationService.getCurrentPeriodRentForRow(row).subscribe({
      next: res => {
        if (res?.currentYearRent != null) {
          row.yearRentInCurrentYear = res.currentYearRent;
        }
        if (res?.periodRent != null) {
          row.rentThisPeriod = res.periodRent;
        }
        this.ensureMarkDirtyAndRefreshRows(changeOrigin, false);
      },
      error: error => {
        this.messageService.showError(error);
      },
    });
  }

  private initBaselinePropertyTaxRow(): void {
    if (this.dataContext?.isNew) return;
    if (this._baselinePropertyTaxRowRef) return;

    const active = getLatestActivePropertyTaxRow(this.dataContext?.leaseAgreementRowTemplates ?? [], { onlyPersisted: true });

    if (!active) return;

    this._baselinePropertyTaxRowRef = active;
    this._baselinePropertyTaxArticleId = (active.article as any)?.id;
    this._baselinePropertyTaxCalculationBasis = this.dataContext?.leaseAgreementTemplate?.propertyTaxCalculationBasis ?? null;
    this._baselinePropertyTaxCalculationMethod = this.dataContext?.leaseAgreementTemplate?.propertyTaxCalculationMethod ?? null;
    this._baselinePropertyTaxRowOriginalToDate = active.toDate && active.toDate.hasValue() ? LocalDate.fromDate(active.toDate) : undefined;
  }

  private getCurrentPropertyTaxBaselineSignature(selectedArticleId?: string | null): PropertyTaxRowSignature {
    return buildPropertyTaxRowSignature(
      selectedArticleId ?? this.templateForm?.get('propertyTaxArticlesNonRetroactive')?.value ?? null,
      this.templateForm?.get('propertyTaxCalculationBasis')?.value ?? null,
      this.templateForm?.get('propertyTaxCalculationMethod')?.value ?? null
    );
  }

  private ensureMarkDirtyAndRefreshRows(changeOrigin: PropertyTaxChangeOrigin, markDirty = true): void {
    this.dataContext.leaseAgreementRowTemplates = [...(this.dataContext.leaseAgreementRowTemplates ?? [])];
    if (
      this._propertyTaxFormState.shouldMarkDirty(changeOrigin, markDirty, this.getPropertyTaxDirtyIntentSnapshot()) &&
      this.form?.pristine
    ) {
      this.form.markAsDirty();
    }
    this.onItemSourceChange(this.dataContext.leaseAgreementRowTemplates);
  }

  private refreshPropertyTaxDirtyBaseline(): void {
    this._propertyTaxFormState.refreshDirtyBaseline(this.getPropertyTaxDirtyIntentSnapshot());
  }

  private getPropertyTaxDirtyIntentSnapshot(): string {
    const intent: PropertyTaxDirtyIntent = {
      rentalObjectId: this._propertyTaxCalculationService.normalizeId(this.dataContext?.id),
      propertyTaxPaidBy:
        this.templateForm?.get('propertyTaxPaidBy')?.value ?? this.dataContext?.leaseAgreementTemplate?.propertyTaxPaidBy ?? null,
      propertyTaxCalculationBasis:
        this.templateForm?.get('propertyTaxCalculationBasis')?.value ??
        this.dataContext?.leaseAgreementTemplate?.propertyTaxCalculationBasis ??
        null,
      propertyTaxPercentage: this.templateForm?.get('propertyTaxPercentage')?.value ?? null,
      propertyTaxCalculationMethod:
        this.templateForm?.get('propertyTaxCalculationMethod')?.value ??
        this.dataContext?.leaseAgreementTemplate?.propertyTaxCalculationMethod ??
        null,
      propertyTaxArticleId:
        this.templateForm?.get('propertyTaxArticlesNonRetroactive')?.value ??
        this.dataContext?.leaseAgreementTemplate?.propertyTaxArticleId ??
        null,
      propertyTaxRetroactiveArticleId:
        this.templateForm?.get('propertyTaxArticlesRetroactive')?.value ??
        this.dataContext?.leaseAgreementTemplate?.propertyTaxRetroArticleId ??
        null,
    };

    return JSON.stringify(intent);
  }
  propertyMeters: Meter[];

  private _buildings: Building[];
  buildings: any[];

  private _entrances: Entrance[];
  entrances: any[];

  calculationBasisOptions = [
    { name: PropertyTaxCalculationBasisToString(PropertyTaxCalculationBasis.Area), value: PropertyTaxCalculationBasis.Area },
    { name: PropertyTaxCalculationBasisToString(PropertyTaxCalculationBasis.Rent), value: PropertyTaxCalculationBasis.Rent },
    { name: PropertyTaxCalculationBasisToString(PropertyTaxCalculationBasis.Percentage), value: PropertyTaxCalculationBasis.Percentage },
  ];

  payedByOptions = [
    { name: PropertyTaxPaidByToString(PropertyTaxPaidBy.Landlord), value: PropertyTaxPaidBy.Landlord },
    { name: PropertyTaxPaidByToString(PropertyTaxPaidBy.Tenant), value: PropertyTaxPaidBy.Tenant },
  ];

  propertyTaxCalculationMethods = [
    {
      name: PropertyTaxCalculationMethodToString(PropertyTaxCalculationMethod.PerProperty),
      value: PropertyTaxCalculationMethod.PerProperty,
    },
    {
      name: PropertyTaxCalculationMethodToString(PropertyTaxCalculationMethod.PerBuilding),
      value: PropertyTaxCalculationMethod.PerBuilding,
    },
  ];

  private _filterBuildingsByProperty(property: string) {
    if (!this._buildings) return;

    this.buildings = this._buildings
      .filter(b => b.property === property)
      .map(b => {
        return { name: b.toString(), value: b.id };
      });

    if (this.buildings.length == 1 && this.isNew) {
      let buildingId = this.buildings[0].value;
      this.form.controls[formControlName.building].setValue(buildingId);
      this.form.controls[formControlName.entrance].setValue(this._buildings.find(b => b.id === buildingId).entrances[0].id);
    }
  }

  private _filterEntranceByBuilding(building: string) {
    if (!this._entrances) return;

    this.entrances = this._entrances
      .filter(b => b.building === building)
      .map(b => {
        return { name: b.name, value: b.id };
      });

    if (this.entrances.length == 1 && this.isNew) {
      let entranceId = this.entrances[0].value;
      this.form.controls[formControlName.entrance].setValue(entranceId);
    }
  }

  private _setBuildingFloors(buildingId: string) {
    let control = this.form.controls[formControlName.buildingFloors];
    if (!control) return;

    let building = this._buildings?.find(b => b.id === buildingId);
    control.setValue(building ? building.floorsInBuilding : null);
  }

  protected getFormGroup(): FormGroup {
    let group = super.getFormGroup();
    this.brfForm = this.fb.group({
      share: this.cf.create('COOPERATIVE_HOUSING.SHARE', this.dataContext.brfSpecifics ? this.dataContext.brfSpecifics.share : null),
      sharePercentage: this.cf.createReadonly(
        'COOPERATIVE_HOUSING.SHARE_PERCENT',
        this.dataContext.brfSpecifics ? this.dataContext.brfSpecifics.sharePercentage : null
      ),
      shareCompanySum: this.cf.createReadonly(
        'COOPERATIVE_HOUSING.SHARE_COMPANY_SUM',
        this.dataContext.brfSpecifics ? this.dataContext.brfSpecifics.shareCompanySum : null
      ),
      contributionAmount: this.cf.create(
        'COOPERATIVE_HOUSING.CONTRIBUTION_AMOUNT',
        this.dataContext.brfSpecifics ? this.dataContext.brfSpecifics.contributionAmount : new Money(this.dataContext.currencyCode, null)
      ),
      baseAnnualFee: this.cf.create(
        'COOPERATIVE_HOUSING.BASE_ANNUAL_FEE',
        this.dataContext.brfSpecifics ? this.dataContext.brfSpecifics.baseAnnualFee : new Money(this.dataContext.currencyCode, null)
      ),
      assignmentFee: this.cf.create(
        'COOPERATIVE_HOUSING.ASSIGNMENT_FEE',
        this.dataContext.brfSpecifics ? this.dataContext.brfSpecifics.assignmentFee : new Money(this.dataContext.currencyCode, null)
      ),
      assignmentDate: this.cf.create(
        'COOPERATIVE_HOUSING.ASSIGNMENT_DATE',
        this.dataContext.brfSpecifics ? this.dataContext.brfSpecifics.assignmentDate : null
      ),
      economicalPlanNumber: this.cf.create(
        'COOPERATIVE_HOUSING.ECONOMICAL_PLAN_NUMBER',
        this.dataContext.brfSpecifics ? this.dataContext.brfSpecifics.economicalPlanNumber : null
      ),
    });

    group.addControl('building', this.cf.createMandatory('DWELLING_FORM.BUILDING_LABEL', this.dataContext.buildingId));
    group.addControl(
      formControlName.buildingFloors,
      this.cf.createWithOptions('DWELLING_FORM.BUILDING_FLOORS_LABEL', null, { isReadonly: true, noSubmit: true })
    );
    group.addControl(
      'indvidualmeterelectricity',
      this.cf.create('PREMISES_FORM.INDIVIDUAL_METER_ELECTRICITY_LABEL', this.dataContext.indvidualMeterElectricity)
    );
    group.addControl(
      'indvidualmeterwater',
      this.cf.create('PREMISES_FORM.INDIVIDUAL_METER_WATER_LABEL', this.dataContext.indvidualMeterWater)
    );
    group.addControl('floor', this.cf.create('DWELLING_FORM.FLOOR_LABEL', this.dataContext.floor));
    group.addControl('numberOfRooms', this.cf.create('DWELLING_FORM.NUMBER_OF_ROOMS_LABEL', this.dataContext.numberOfRooms));
    group.addControl('entrance', this.cf.createMandatory('ENTRANCE.ENTRANCE', this.dataContext.entranceId));
    group.addControl('surface', this.cf.create('RENTAL_OBJECT_FORM.SURFACE_LOA_LONG', this.dataContext.surface));
    group.addControl('formOfLease', this.cf.create('RENTAL_OBJECT.FORM_OF_LEASE', this.dataContext.formOfLease));
    group.addControl(
      'imdMeterElectricity',
      this.createMeterForm(
        group,
        this.dataContext.imdMeterElectricity,
        'CONSUMPTION.METER_NAME.ELECTRICITY',
        'DWELLING_FORM.INDIVIDUAL_METER_ELECTRICITY_LABEL',
        this.dataContext.indvidualMeterElectricity,
        formControlName.indvidualmeterelectricity
      )
    );
    group.addControl(
      'imdMeterColdWater',
      this.createMeterForm(
        group,
        this.dataContext.imdMeterColdWater,
        'CONSUMPTION.METER_NAME.COLD_WATER',
        'DWELLING_FORM.INDIVIDUAL_METER_COLD_WATER_LABEL',
        this.dataContext.indvidualMeterColdWater,
        formControlName.indvidualmetercoldwater
      )
    );
    group.addControl(
      'imdMeterHotWater',
      this.createMeterForm(
        group,
        this.dataContext.imdMeterHotWater,
        'CONSUMPTION.METER_NAME.HOT_WATER',
        'DWELLING_FORM.INDIVIDUAL_METER_WATER_LABEL',
        this.dataContext.indvidualMeterWater,
        formControlName.indvidualmeterwater
      )
    );
    group.addControl(
      'imdMeterHeating',
      this.createMeterForm(
        group,
        this.dataContext.imdMeterHeating,
        'CONSUMPTION.METER_NAME.HEATING',
        'DWELLING_FORM.INDIVIDUAL_METER_HEATING_LABEL',
        this.dataContext.indvidualMeterHeating,
        formControlName.indvidualmeterheating
      )
    );
    group.addControl(
      'propertyTaxAssessmentUnitType',
      this.cf.createReadonly('PROPERTY_TAX.CALCULATION_BASIS_BY_TAXTYPECODE', this.propertyTaxAssessmentUnitTypeString)
    );
    const ctrl = group.get('propertyTaxAssessmentUnitType');
    ctrl?.disable({ emitEvent: false });
    this.isPercentage = this.dataContext.leaseAgreementTemplate?.propertyTaxCalculationBasis === PropertyTaxCalculationBasis.Percentage;

    group.addControl('brfSpecifics', this.brfForm);
    group.addControl('rentalObjectCustomProperties', this._createPropertiesForm(this.dataContext.rentalObjectCustomProperties));
    return group;
  }

  customProperties: {
    form: FormGroup;
    selection: SelectionItem[];
    showInt: boolean;
    showText: boolean;
    showDate: boolean;
    showBoolean: boolean;
    showSelection: boolean;
    showNumeric: boolean;
  }[];

  private _createPropertiesForm(rentalObjectCustomProperties: LinkedCustomProperty[]): FormArray {
    const formPropertiesArray = new UntypedFormArray([]);
    this.customProperties = [];

    for (const rentalProperty of rentalObjectCustomProperties ?? []) {
      const form = this.fb.group({
        id: this.cf.createHidden(rentalProperty.id),
        customPropertyId: this.cf.createHidden(rentalProperty.customPropertyId),
        numericValue: this.cf.create(rentalProperty.caption, rentalProperty.numericValue),
        textValue: this.cf.create(rentalProperty.caption, rentalProperty.textValue),
        dateValue: this.cf.create(rentalProperty.caption, LocalDate.fromDate(rentalProperty.dateValue)),
        boolValue: this.cf.create(rentalProperty.caption, rentalProperty.boolValue),
        dataType: this.cf.create(rentalProperty.caption, rentalProperty.dataType),
      });
      formPropertiesArray.push(form);

      this.customProperties.push({
        form,
        selection: rentalProperty.selectableValues.map(x => new SelectionItem(x, x)),
        showInt: rentalProperty.dataType === CustomPropertyDataType.Numeric && rentalProperty.decimalCount === 0,
        showText: rentalProperty.dataType === CustomPropertyDataType.Text,
        showDate: rentalProperty.dataType === CustomPropertyDataType.Date,
        showBoolean: rentalProperty.dataType === CustomPropertyDataType.Boolean,
        showSelection: rentalProperty.dataType === CustomPropertyDataType.Selection,
        showNumeric: rentalProperty.dataType === CustomPropertyDataType.Numeric && rentalProperty.decimalCount !== 0,
      });
    }

    return formPropertiesArray;
  }

  isPercentage: boolean = false;
  readonly propertyTaxSelectionTrackBy = (item: SelectionItem): unknown => item?.value ?? item?.name;
  readonly propertyTaxLeaseRowTrackBy = (row: LeaseRow): unknown => row?.id ?? this.getLeaseRowClientTrackKey(row);
  private readonly _leaseRowClientTrackKeys = new WeakMap<LeaseRow, string>();
  private _leaseRowClientTrackKeySequence = 0;

  get canEditPropertyTaxFields(): boolean {
    return this.templateForm?.get('propertyTaxPaidBy')?.value !== PropertyTaxPaidBy.Landlord;
  }

  private getLeaseRowClientTrackKey(row: LeaseRow): string | null {
    if (!row) return null;

    let key = this._leaseRowClientTrackKeys.get(row);
    if (!key) {
      key = `premises-form-lease-row-${++this._leaseRowClientTrackKeySequence}`;
      this._leaseRowClientTrackKeys.set(row, key);
    }
    return key;
  }

  private updatePropertyTaxValidators(): void {
    const propertyTaxPaidBy = this.templateForm?.get('propertyTaxPaidBy')?.value;
    const calculationBasis = this.templateForm?.get('propertyTaxCalculationBasis')?.value;
    const requiresPropertyTaxFields = propertyTaxPaidBy === PropertyTaxPaidBy.Tenant;
    const requiresPercentage = requiresPropertyTaxFields && calculationBasis === PropertyTaxCalculationBasis.Percentage;

    ['propertyTaxCalculationBasis', 'propertyTaxCalculationMethod', 'propertyTaxArticlesNonRetroactive'].forEach(controlName => {
      const control = this.templateForm?.get(controlName) as any;
      if (!!control.required !== requiresPropertyTaxFields) {
        control.required = requiresPropertyTaxFields;
      }
      control?.updateValueAndValidity({ emitEvent: false });
    });

    const percentageControl = this.templateForm?.get('propertyTaxPercentage') as any;
    if (!!percentageControl.required !== requiresPercentage) {
      percentageControl.required = requiresPercentage;
    }
    percentageControl?.updateValueAndValidity({ emitEvent: false });

    this.templateForm?.updateValueAndValidity({ emitEvent: false });
  }

  private createMeterForm(
    form: FormGroup,
    meter: RentalObjectConsumptionMeter,
    nameLabel: string,
    enableLabel: string,
    enabled: boolean,
    enableField: string
  ) {
    let group = this.fb.group({
      id: this.cf.createHidden(meter ? meter.id : null),
      name: this.cf.create(nameLabel, meter ? meter.name : null),
      imdMeterModelId: this.cf.create('CONSUMPTION.METER_MODEL.NAME', meter ? meter.imdMeterModelId : null),
      imdMeterId: this.cf.create('CONSUMPTION.METER.METER', meter ? meter.imdMeterId : null),
      enabled: this.cf.create(enableLabel, enabled),
    });

    this.AddSubscription(
      group.controls['enabled'].valueChanges.subscribe(val => {
        form.controls[enableField].setValue(val);
      })
    );

    this.AddSubscription(
      group.controls['imdMeterId'].valueChanges.subscribe(val => {
        let meter = this.propertyMeters.find(m => m.id === val);
        if (meter) {
          group.controls['imdMeterModelId'].setValue(meter.imdMeterModelId);
        }
      })
    );

    return group;
  }

  propertyTaxForm: FormGroup<any>;

  get showBrf(): boolean {
    let formOfLease = this.form.controls['formOfLease'].value;
    return formOfLease === FormOfLease.CooperativeHousing || formOfLease === FormOfLease.Ownership;
  }

  get propertyTaxAssessmentUnitTypeString(): string {
    return getPropertyTaxAssessmentUnitTypeString(this.dataContext?.propertyTaxAssessmentUnitType, this._languageService);
  }

  get propertyTaxRentalChangeForLeaseRows(): PropertyTaxRentalChangeModel | undefined {
    if (this._draftPropertyTaxRentalChange !== undefined) {
      return this._draftPropertyTaxRentalChange ?? undefined;
    }

    return this.dataContext?.leaseAgreementTemplate?.propertyTaxRentalChange;
  }

  brfForm: FormGroup;

  private _getAgreementPath(category: LeaseAgreementCategory) {
    let segment = '';
    switch (category) {
      case LeaseAgreementCategory.Block:
      case LeaseAgreementCategory.Dwelling:
      case LeaseAgreementCategory.Parking:
      case LeaseAgreementCategory.Premises:
        segment = LeaseAgreementCategory[category].toLowerCase();
        break;

      case LeaseAgreementCategory.Other:
        segment = 'other-rental-object';
        break;

      case LeaseAgreementCategory.CooperativeHousing:
        segment = 'co-housing';
        break;

      default:
        throw new Error(`The specified contract category is not supported. Category: ${category}`);
    }
    return segment;
  }

  onRowClicked(e: { context: Contract; event: any }): void {
    this.navigation.navigate([this._getAgreementPath(e.context.category), 'contracts', 'edit', e.context.id], null, e.event.ctrlKey);
  }

  onApplicationRowClicked(e: { context: RentApplication; event: any }): void {
    this.navigation.navigate(['rent-application', 'edit', e.context.id], null, e.event.ctrlKey);
  }

  onAdvertismentRowClicked(e: { context: LettingAdvertisment; event: any }): void {
    this.navigation.navigateChild([this.dataContext.id, 'advertisments', 'edit', e.context.id], null, e.event.ctrlKey);
  }

  protected createModel(): Premises {
    return new Premises();
  }

  allowedFormsOfLease: SelectionItem[] = [];

  get heatingPaymentOptions() {
    return this.showBrf ? this.heatingPaymentsBrf : this.heatingPayments;
  }

  get coldWaterPaymentOptions() {
    return this.showBrf ? this.coldWaterPaymentsBrf : this.coldWaterPayments;
  }

  get hotWaterPaymentOptions() {
    return this.showBrf ? this.hotWaterPaymentsBrf : this.hotWaterPayments;
  }

  get householdElectricityPaymentOptions() {
    return this.showBrf ? this.householdElectricityPaymentsBrf : this.householdElectricityPayments;
  }

  private _propertyTaxArticleChangeSub?: Subscription;
  private _propertyTaxDraftCoordinator?: PropertyTaxDraftCoordinator;
  private _propertyTaxCurrentYearRentSub?: Subscription;
  private _draftPropertyTaxRentalChange?: PropertyTaxRentalChangeModel | null;
  private _nonRetroPropertyTaxRowRef?: LeaseRow;
  private _nonRetroPropertyTaxRowCreatedByUi = false;
  private _baselinePropertyTaxRowRef?: LeaseRow;
  private _baselinePropertyTaxArticleId?: string;
  private _baselinePropertyTaxCalculationBasis?: PropertyTaxCalculationBasis | null;
  private _baselinePropertyTaxCalculationMethod?: PropertyTaxCalculationMethod | null;
  private _baselinePropertyTaxRowOriginalToDate?: LocalDate | null;
  private _baselinePropertyTaxRowClosedByUi = false;
  private _uiPropertyTaxRowRef?: LeaseRow;
  private _uiPropertyTaxRowCreatedByUi = false;
  private _suspendPropertyTaxUserTriggers = true;

  protected init(premises: Premises) {
    runWithPropertyTaxUserTriggersSuspended(
      value => (this._suspendPropertyTaxUserTriggers = value),
      () => {
        if (premises.leaseAgreementTemplate?.propertyTaxPaidBy == null) {
          premises.leaseAgreementTemplate.propertyTaxPaidBy = PropertyTaxPaidBy.Landlord;
        }
        super.init(premises);
        this.changedAt = premises.changedAt;

        this._propertyTaxArticleChangeSub?.unsubscribe();
        this._propertyTaxDraftCoordinator?.dispose();
        this._propertyTaxCurrentYearRentSub?.unsubscribe();
        this._draftPropertyTaxRentalChange = undefined;
        this._nonRetroPropertyTaxRowRef = undefined;
        this._nonRetroPropertyTaxRowCreatedByUi = false;
        this._baselinePropertyTaxRowRef = undefined;
        this._baselinePropertyTaxArticleId = undefined;
        this._baselinePropertyTaxCalculationBasis = undefined;
        this._baselinePropertyTaxCalculationMethod = undefined;
        this._baselinePropertyTaxRowOriginalToDate = undefined;
        this._baselinePropertyTaxRowClosedByUi = false;
        this._uiPropertyTaxRowRef = undefined;
        this._uiPropertyTaxRowCreatedByUi = false;
        this._propertyTaxFormState.setPendingDraftChangeOrigin('hydration');
        this.initializePropertyTaxDraftCoordinator();
        this.bindPropertyTaxArticleChange();
        this.bindPropertyTaxDraftRecalculation();
        this.initBaselinePropertyTaxRow();
        this.scheduleDraftPropertyTaxRecalculation('hydration');
        this.refreshPropertyTaxDirtyBaseline();
      }
    );

    this.allowedFormsOfLease = premises.allowedFormsOfLease
      ? premises.allowedFormsOfLease.map(f => new SelectionItem(FormOfLeaseToString(f), f))
      : [];

    this.AddSubscription(
      this.form.controls['company'].valueChanges
        .pipe(switchMap(value => this._dataService.getValidFormsOfLease(value)))
        .subscribe(formsOfLease => {
          this.allowedFormsOfLease = formsOfLease.map(f => new SelectionItem(FormOfLeaseToString(f), f));
          if (!this.allowedFormsOfLease.find(f => f.value === this.form.controls['formOfLease'].value)) {
            this.form.controls['formOfLease'].setValue(this.allowedFormsOfLease[0].value);
          }
        })
    );

    this.AddSubscription(
      this.templateForm.controls['propertyTaxPaidBy'].valueChanges
        .pipe(
          tap(() => {
            this.updatePropertyTaxValidators();
            this.scheduleDraftPropertyTaxRecalculation('user');
          })
        )
        .subscribe()
    );

    this.AddSubscription(
      this.templateForm.controls['propertyTaxCalculationBasis'].valueChanges
        .pipe(
          tap(value => {
            this.isPercentage = value != null && value === PropertyTaxCalculationBasis.Percentage;
            this.updatePropertyTaxValidators();
            this.handleCurrentPropertyTaxSelectionChange('user');
            this.scheduleDraftPropertyTaxRecalculation('user');
          })
        )
        .subscribe()
    );

    this.AddSubscription(
      this.templateForm.controls['propertyTaxCalculationMethod'].valueChanges
        .pipe(
          tap(() => {
            this.handleCurrentPropertyTaxSelectionChange('user');
          })
        )
        .subscribe()
    );

    this.updatePropertyTaxValidators();

    this._buildingService.getList().subscribe(data => {
      this._buildings = data;
      if (!this.buildings) {
        let property = this.form.controls['property'].value;
        if (property) {
          this._filterBuildingsByProperty(property);
        }
        this._setBuildingFloors(this.form.controls['building'].value);
      }
    });

    this._entranceService.getList().subscribe(data => {
      this._entrances = data;
      if (!this.entrances) {
        let building = this.form.controls['building'].value;
        if (building) {
          this._filterEntranceByBuilding(building);
        }
      }
    });

    this.form.controls['building'].valueChanges.subscribe(value => {
      this._setBuildingFloors(value);
      if (this._buildings) {
        let b = this._buildings.find(b => b.id === value);
        if (b) {
          this.setAddress(b.address);
          this.form.controls['entrance'].setValue(b.entrances[0].id);
        }
      }
    });

    this.form.controls['property'].valueChanges.subscribe(value => {
      this._filterBuildingsByProperty(value);
    });

    this.form.controls['building'].valueChanges.subscribe(value => {
      this._filterEntranceByBuilding(value);
    });

    if (premises.propertyId) {
      this.AddSubscription(this._meterService.getPropertyMeters(premises.propertyId).subscribe(meters => (this.propertyMeters = meters)));
    }

    this.AddSubscription(
      this.form.controls['property'].valueChanges
        .pipe(
          tap(value => {
            this._filterBuildingsByProperty(value);
          }),
          switchMap(value => this._meterService.getPropertyMeters(value))
        )
        .subscribe(meters => {
          this.propertyMeters = meters;
        })
    );
  }

  protected override initializeSection(section: string): void {
    if (section === 'advertisments' && !this.isNew) {
      this.AddSubscription(
        this.advertismentService.getRentalObjectAdvertisments(this.dataContext.id).subscribe({
          next: result => (this.advertisments = result),
        })
      );
    }

    super.initializeSection(section);
  }

  override ngOnDestroy(): void {
    this._propertyTaxDraftCoordinator?.dispose();
    this._propertyTaxCurrentYearRentSub?.unsubscribe();
    super.ngOnDestroy();
  }

  private initializePropertyTaxDraftCoordinator(): void {
    this._propertyTaxDraftCoordinator?.dispose();
    this._propertyTaxDraftCoordinator = this._propertyTaxCalculationService.createDraftCoordinator<
      PremisesDraftPropertyTaxCalculationRequest,
      PropertyTaxDraftResult<PropertyTaxRentalChangeModel>
    >({
      createRequest: () => this.createDraftPropertyTaxRequest(),
      executeRequest: request => {
        const changeOrigin = this._propertyTaxFormState.pendingDraftChangeOrigin;
        return this._propertyTaxCalculationService.calculateDraftPropertyTax(request).pipe(map(response => ({ changeOrigin, response })));
      },
      onResult: result => {
        const response = result?.response ?? null;
        const changeOrigin = result?.changeOrigin ?? 'hydration';
        this._draftPropertyTaxRentalChange = response === null ? null : response;

        if (!this.dataContext?.leaseAgreementTemplate) {
          return;
        }

        this.dataContext.leaseAgreementTemplate.propertyTaxRentalChange = response === null ? null : response;
        const articleId = this.templateForm?.get('propertyTaxArticlesNonRetroactive')?.value ?? null;
        if (articleId && response) {
          this.applyDraftPropertyTaxToCurrentRow(articleId, response, changeOrigin);
        }
      },
      onError: error => {
        this.messageService.showError(error);
      },
    });
  }

  protected prepareSave(): Premises {
    let model = super.prepareSave();
    model.changedAt = this.changedAt;
    if (!model.brfSpecifics.assignmentFee || !model.brfSpecifics.assignmentFee.amount)
      model.brfSpecifics.assignmentFee = new Money(model.currencyCode, 0);
    if (!model.brfSpecifics.baseAnnualFee || !model.brfSpecifics.baseAnnualFee.amount)
      model.brfSpecifics.baseAnnualFee = new Money(model.currencyCode, 0);
    if (!model.brfSpecifics.contributionAmount || !model.brfSpecifics.contributionAmount.amount)
      model.brfSpecifics.contributionAmount = new Money(model.currencyCode, 0);
    model.buildingId = this.form.controls['building'].value;
    model.entranceId = this.form.controls['entrance'].value;

    model.leaseAgreementTemplate.propertyTaxPaidBy = this.templateForm.get('propertyTaxPaidBy')?.value;
    if (this.canEditPropertyTaxFields) {
      model.leaseAgreementTemplate.propertyTaxCalculationBasis = this.templateForm.get('propertyTaxCalculationBasis')?.value;
      model.leaseAgreementTemplate.propertyTaxPercentage = this.isPercentage
        ? this.templateForm.get('propertyTaxPercentage')?.value / 100
        : null;
      model.leaseAgreementTemplate.propertyTaxCalculationMethod = this.templateForm.get('propertyTaxCalculationMethod')?.value;
      model.leaseAgreementTemplate.propertyTaxRetroArticleId = this.templateForm.get('propertyTaxArticlesRetroactive')?.value;
      model.leaseAgreementTemplate.propertyTaxArticleId = this.templateForm.get('propertyTaxArticlesNonRetroactive')?.value;
    } else {
      model.leaseAgreementTemplate.propertyTaxCalculationBasis = null;
      model.leaseAgreementTemplate.propertyTaxPercentage = null;
      model.leaseAgreementTemplate.propertyTaxCalculationMethod = null;
      model.leaseAgreementTemplate.propertyTaxRetroArticleId = null;
      model.leaseAgreementTemplate.propertyTaxArticleId = null;
    }
    return model;
  }

  protected override formSubmittedNew(id: string): void {
    super.formSubmittedNew(id);
    this.refreshPropertyTaxDirtyBaseline();
  }

  override formSubmittedChange(response?: any) {
    this.changedAt = response;
    super.formSubmittedChange(response);
    this.refreshPropertyTaxDirtyBaseline();
  }
}
